"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import { useBootPrefetch } from "@/hooks/useBootPrefetch";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { getOnboardingRedirect } from "@/utils/getOnboardingRedirect";
import {
  useGetCustomerAccountApplicationsQuery,
  useGetSelfQuery,
} from "@generated/redux/services/generated/api";

// Cross-page-load cap on the OPENED-state reload below. window.location
// .reload() resets all in-memory state (the hasRedirected ref included), so
// if /self persistently returns no customer account while the application is
// OPENED (provisioning lag, backend inconsistency), an uncapped reload loops
// forever — each cycle too short for even the 8s splash watchdog to fire.
const OPENED_RELOADS_KEY = "onboarding.openedReloads";
const MAX_OPENED_RELOADS = 2;

const readOpenedReloads = (): number => {
  try {
    return Number(window.sessionStorage.getItem(OPENED_RELOADS_KEY) ?? "0");
  } catch {
    // Storage unavailable — treat as first attempt; the cap degrades to
    // an uncapped (pre-existing) behaviour rather than blocking the reload.
    return 0;
  }
};

/**
 * Checks if the authenticated user has a customer account.
 * If not, checks application state and redirects to onboarding or status page.
 * Rendered inside the (customerAccount) layout, before CustomerAccountProvider.
 */
export function OnboardingRouter({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const hasRedirected = useRef(false);

  const { data: selfData, isLoading: selfLoading } = useGetSelfQuery();
  // Mounted while /self is pending (CustomerAccountProvider is not), so this
  // is where the returning user's balances and ledger prefetch can start.
  useBootPrefetch();
  const hasCustomerAccount =
    (selfData?.customerAccounts && selfData.customerAccounts.length > 0) ??
    false;

  // Only fetch applications if user has no customer account and self data loaded
  const { data: applicationsData, isLoading: appsLoading } =
    useGetCustomerAccountApplicationsQuery(
      {},
      {
        skip: selfLoading || !selfData || hasCustomerAccount,
      }
    );

  useEffect(() => {
    if (selfLoading || !selfData || hasRedirected.current) return;
    if (hasCustomerAccount) {
      // Account arrived — clear the reload budget for future sessions.
      try {
        window.sessionStorage.removeItem(OPENED_RELOADS_KEY);
      } catch {
        // Storage unavailable — nothing to clean up.
      }
      return;
    }
    if (appsLoading) return;

    hasRedirected.current = true;

    const redirect = getOnboardingRedirect(false, applicationsData?.data ?? []);

    if (redirect === null) {
      // OPENED state — account should exist, reload to pick it up. Capped:
      // past the budget we stop reloading and report, leaving the splash up
      // so its watchdog + "Reload app" button take over as the recovery.
      const reloads = readOpenedReloads();
      if (reloads >= MAX_OPENED_RELOADS) {
        reportSilentFailure({
          message:
            "[Onboarding] Application OPENED but no customer account after reloads",
          fingerprint: "onboarding-opened-reload-loop",
          extra: { reloads },
        });
        return;
      }
      try {
        window.sessionStorage.setItem(OPENED_RELOADS_KEY, String(reloads + 1));
      } catch {
        // Storage unavailable — proceed with the reload regardless.
      }
      window.location.reload();
    } else {
      router.replace(redirect);
    }
  }, [
    selfLoading,
    selfData,
    hasCustomerAccount,
    appsLoading,
    applicationsData,
    router,
  ]);

  // While loading, show nothing
  if (selfLoading) return null;

  // No self data (error/retry) — pass through so SplashScreen can show error UI
  if (!selfData) return <>{children}</>;

  // No customer accounts — show nothing while redirect happens
  if (!hasCustomerAccount) return null;

  return <>{children}</>;
}
